import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Building2, Send, CheckCircle, Printer, Truck, Users, Award } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const SERVICE_OPTIONS = [
  { value: 'offset', label: 'Offset Printing' },
  { value: 'digital', label: 'Digital Printing' },
  { value: 'large_format', label: 'Large Format' },
  { value: 'packaging', label: 'Packaging' },
  { value: 'screen_printing', label: 'Screen Printing' },
  { value: 'embroidery', label: 'Embroidery' },
  { value: 'laser_cutting', label: 'Laser Cutting / Engraving' },
  { value: 'binding', label: 'Binding & Finishing' },
];

const BENEFITS = [
  { icon: Printer, title: 'Steady Order Flow', desc: 'Receive regular printing jobs from our growing customer base' },
  { icon: Users, title: 'Network Growth', desc: 'Connect with businesses and individuals across Lebanon' },
  { icon: Truck, title: 'Logistics Support', desc: 'We handle customer relations and delivery coordination' },
  { icon: Award, title: 'Quality Recognition', desc: 'Get featured as a verified Printed LB production partner' },
];

export function PartnerPage() {
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    company_name: '', contact_name: '', email: '', phone: '', city: '',
    services: [], equipment: '', experience_years: '', website: '', message: '',
  });

  const toggleService = (value) => {
    setForm(prev => ({
      ...prev,
      services: prev.services.includes(value)
        ? prev.services.filter(s => s !== value)
        : [...prev.services, value],
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.company_name || !form.contact_name || !form.email || !form.phone || !form.city) {
      toast.error('Please fill in all required fields');
      return;
    }
    if (form.services.length === 0) {
      toast.error('Please select at least one service');
      return;
    }
    setLoading(true);
    try {
      const body = {
        ...form,
        experience_years: form.experience_years ? parseInt(form.experience_years) : null,
        website: form.website || null,
        message: form.message || null,
        equipment: form.equipment || null,
      };
      const res = await fetch(`${API}/api/partner-applications`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (!res.ok) throw new Error('Submission failed');
      setSubmitted(true);
      toast.success('Application submitted!');
    } catch {
      toast.error('Failed to submit application. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center py-20 px-4">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="bg-white rounded-3xl p-12 border border-gray-200 max-w-lg w-full text-center"
        >
          <div className="w-20 h-20 mx-auto bg-mint/10 rounded-full flex items-center justify-center mb-6">
            <CheckCircle className="h-10 w-10 text-mint" />
          </div>
          <h2 className="text-2xl font-heading font-bold text-navy" data-testid="partner-success-title">Application Submitted!</h2>
          <p className="mt-3 text-gray-600 leading-relaxed">
            Thank you for your interest in joining Printed LB's production network. Our team will review your application and get back to you within 48 hours.
          </p>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen" data-testid="partner-page">
      {/* Hero */}
      <section className="bg-navy py-20 lg:py-28">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="max-w-3xl"
          >
            <span className="text-sm text-mint uppercase tracking-[0.3em] font-semibold">
              Partner With Us
            </span>
            <h1 className="mt-4 text-4xl sm:text-5xl lg:text-6xl font-heading font-black text-white tracking-tighter leading-[1.05]">
              Join Our <span className="text-mint">Production Team</span>
            </h1>
            <p className="mt-6 text-lg text-gray-300 leading-relaxed max-w-xl">
              Are you a print house looking for new business? Partner with Printed LB and become part of our growing network of production partners across Lebanon.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-16 bg-white border-b border-gray-100">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-8">
            {BENEFITS.map((b, i) => (
              <motion.div
                key={b.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.1 }}
                className="text-center"
              >
                <div className="w-14 h-14 mx-auto bg-mint/10 rounded-2xl flex items-center justify-center mb-4">
                  <b.icon className="h-7 w-7 text-mint" />
                </div>
                <h3 className="font-heading font-semibold text-navy">{b.title}</h3>
                <p className="text-sm text-gray-500 mt-1">{b.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Application Form */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-2xl">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <div className="text-center mb-10">
              <Building2 className="h-10 w-10 text-mint mx-auto mb-4" />
              <h2 className="text-3xl font-heading font-bold text-navy">Apply Now</h2>
              <p className="mt-2 text-gray-500">Fill out the form below and we'll get in touch</p>
            </div>

            <form onSubmit={handleSubmit} className="bg-white rounded-2xl p-8 border border-gray-200 space-y-6" data-testid="partner-application-form">
              {/* Company Info */}
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-navy block mb-1.5">Company Name *</label>
                  <Input
                    value={form.company_name}
                    onChange={(e) => setForm({ ...form, company_name: e.target.value })}
                    placeholder="Your print house name"
                    data-testid="partner-company-name"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-navy block mb-1.5">Contact Person *</label>
                  <Input
                    value={form.contact_name}
                    onChange={(e) => setForm({ ...form, contact_name: e.target.value })}
                    placeholder="Full name"
                    data-testid="partner-contact-name"
                  />
                </div>
              </div>

              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-navy block mb-1.5">Email *</label>
                  <Input
                    type="email"
                    value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                    placeholder="contact@company.com"
                    data-testid="partner-email"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-navy block mb-1.5">Phone *</label>
                  <Input
                    value={form.phone}
                    onChange={(e) => setForm({ ...form, phone: e.target.value })}
                    placeholder="+961 ..."
                    data-testid="partner-phone"
                  />
                </div>
              </div>

              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-navy block mb-1.5">City *</label>
                  <Input
                    value={form.city}
                    onChange={(e) => setForm({ ...form, city: e.target.value })}
                    placeholder="e.g. Beirut"
                    data-testid="partner-city"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-navy block mb-1.5">Years of Experience</label>
                  <Input
                    type="number"
                    value={form.experience_years}
                    onChange={(e) => setForm({ ...form, experience_years: e.target.value })}
                    placeholder="e.g. 10"
                    data-testid="partner-experience"
                  />
                </div>
              </div>

              {/* Services */}
              <div>
                <label className="text-sm font-medium text-navy block mb-2">Services You Offer *</label>
                <div className="grid grid-cols-2 gap-2">
                  {SERVICE_OPTIONS.map(s => (
                    <button
                      key={s.value}
                      type="button"
                      onClick={() => toggleService(s.value)}
                      className={`px-3 py-2.5 rounded-xl text-sm font-medium transition-all border ${
                        form.services.includes(s.value)
                          ? 'bg-mint/10 border-mint text-navy'
                          : 'bg-white border-gray-200 text-gray-600 hover:border-gray-300'
                      }`}
                      data-testid={`partner-service-${s.value}`}
                    >
                      {s.label}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="text-sm font-medium text-navy block mb-1.5">Equipment / Capabilities</label>
                <Input
                  value={form.equipment}
                  onChange={(e) => setForm({ ...form, equipment: e.target.value })}
                  placeholder="e.g. Heidelberg XL 106, Roland wide-format..."
                  data-testid="partner-equipment"
                />
              </div>

              <div>
                <label className="text-sm font-medium text-navy block mb-1.5">Website</label>
                <Input
                  value={form.website}
                  onChange={(e) => setForm({ ...form, website: e.target.value })}
                  placeholder="https://..."
                  data-testid="partner-website"
                />
              </div>

              <div>
                <label className="text-sm font-medium text-navy block mb-1.5">Additional Message</label>
                <Textarea
                  value={form.message}
                  onChange={(e) => setForm({ ...form, message: e.target.value })}
                  rows={3}
                  placeholder="Tell us more about your business..."
                  data-testid="partner-message"
                />
              </div>

              <Button
                type="submit"
                disabled={loading}
                className="w-full bg-mint hover:bg-mint-dark text-black py-6 text-base rounded-full btn-hover"
                data-testid="partner-submit-btn"
              >
                {loading ? 'Submitting...' : (
                  <>
                    <Send className="h-5 w-5 mr-2" /> Submit Application
                  </>
                )}
              </Button>
            </form>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
